import { useMutation, useQuery } from "@apollo/client";
import { message } from "antd";
import React, { useMemo } from "react";
import { MERGE_LEAD_OPPORTUNITY } from "../../../../graphql/mutation";
import { GET_LEAD_OPPORTUNITIES_BY_IDS } from "../../../../graphql/query";
import useLang from "../../../../hooks/useLang";
import useCrm from "../../../../hooks/useCrm";
import MergeRecordsDrawer from "../merge/MergeRecordsDrawer";
import { MERGE_FIELDS, buildMergeVariables } from "./mergeOpportunityHelpers";

/**
 * Merge opportunities (EAI-1815).
 *
 * Opened from the "Duplicate opportunity found" banner on an opportunity card.
 * Supplies the opportunity-specific parts — the duplicates query, the field
 * config and the create mutation — to the shared ``MergeRecordsDrawer``, which
 * owns the comparison UI.
 *
 * The merge itself is entirely server-side. ``mergeLeadOpportunity``
 * (EAI-1871) KEEPS the record the user picked as master and updates it in
 * place, archiving only the others — it no longer creates a new opportunity the
 * way ``createLeadOpportunity(potentialOpportunity:)`` did. The master is
 * therefore passed separately and must not appear in the merge list. Excluded
 * records are left out of that list entirely, which is what keeps them live.
 */

/** The design auto-dismisses the success toast after 3.5s. */
const TOAST_DURATION_SECONDS = 3.5;

const MergeOpportunityDrawer = ({ open, onClose, opportunity, onMerged }) => {
  const [lang] = useLang();
  const { getOpprDetails } = useCrm();

  /**
   * The record's id, linked to the same CRM record its opportunity card links
   * to. Both the BE-resolved URL and the label come from ``getOpprDetails``, so
   * a record is titled and linked here exactly as it is on the card — and an
   * opportunity with no CRM counterpart (``hasCrmId`` false) yields no href
   * rather than a link built from our internal id.
   */
  const getRecordIdLink = record => {
    const { crmLink, fullLabel } = getOpprDetails({ opportunity: record });
    const href = record?.crmUrl || crmLink;
    return href ? { href, title: fullLabel } : null;
  };

  const duplicateIds = useMemo(
    () => (opportunity?.potentialOpportunity || []).map(Number).filter(Boolean),
    [opportunity]
  );

  const { data, loading, error } = useQuery(GET_LEAD_OPPORTUNITIES_BY_IDS, {
    variables: { leadOpportunityIds: duplicateIds },
    // Nothing to fetch until the drawer is open on a card with duplicates.
    skip: !open || !duplicateIds.length,
    // The duplicates are archived by the merge, so never show them from cache.
    fetchPolicy: "network-only",
  });

  /**
   * The card's own opportunity leads, then every duplicate the BE flagged.
   * Only active duplicates take part — one already archived by an earlier merge
   * would otherwise show up as a mergeable column.
   */
  const records = useMemo(() => {
    if (!opportunity) return [];
    const duplicates = (data?.getLeadOpportunitiesById || []).filter(
      opp => opp?.isActive !== false && +opp?.id !== +opportunity?.id
    );
    return [opportunity, ...duplicates];
  }, [opportunity, data]);

  const [mergeLeadOpportunity, { loading: merging }] = useMutation(MERGE_LEAD_OPPORTUNITY, {
    onError: err => message.error(err?.message || lang.somethingWentWrong),
  });

  const handleMerge = async ({ primaryIdx, excluded, picks, includedCount }) => {
    const res = await mergeLeadOpportunity({
      variables: buildMergeVariables({ records, primaryIdx, excluded, picks }),
    });
    const result = res?.data?.mergeLeadOpportunity;

    if (result?.statusCode === 200) {
      onClose?.();
      message.success(
        lang.mergeSuccessToast.replace("{{count}}", includedCount),
        TOAST_DURATION_SECONDS
      );
      // Refresh so the kept record shows its merged values and the archived
      // sources stop being offered as duplicates. A 200 also covers "nothing to
      // merge" — those records were already merged, so the refetch is what
      // clears the stale banner.
      onMerged?.();
      return;
    }

    // 409 means the records moved under us while the drawer sat open (one was
    // archived or merged elsewhere). Nothing the user can fix in place, so
    // close and refetch instead of leaving them staring at a stale comparison.
    if (result?.statusCode === 409) {
      message.error(result?.message || lang.somethingWentWrong);
      onClose?.();
      onMerged?.();
      return;
    }

    // 400 and anything else is a request the user may still be able to fix —
    // keep the drawer open so the selection isn't lost.
    message.error(result?.message || lang.somethingWentWrong);
  };

  return (
    <MergeRecordsDrawer
      open={open}
      onClose={onClose}
      title={lang.mergeOpportunitiesTitle}
      infoBody={lang.mergeInfoPanelBody}
      confirmTitle={lang.mergeConfirmTitle}
      confirmBody={lang.mergeConfirmBody}
      emptyMessage={lang.mergeNoDuplicatesFound}
      records={records}
      fields={MERGE_FIELDS}
      createdKey="createdDate"
      showRecordIds
      getRecordIdLink={getRecordIdLink}
      loading={loading}
      error={error}
      merging={merging}
      onConfirm={handleMerge}
    />
  );
};

export default MergeOpportunityDrawer;
